import { useEffect, useMemo, useRef, useState } from 'react'
import { CHART_GROUPS, chartsInGroup } from '@pharmacia/core'
import type { AnalyticsFilters, ChartId, ChartPayload, FilterKey, GroupId } from '@pharmacia/core'
import { get } from '../lib/api.ts'
import { PageHeader } from '../components/AppShell.tsx'
import { ChartBlock } from '../components/charts/ChartBlock.tsx'
import { FilterBar, rangeFor, type Picks, type Preset } from '../components/charts/FilterBar.tsx'

// Analysis.
//
// The filter bar sits above the charts and survives a change of group, so
// "this doctor, this financial year" stays applied while the question
// moves from money to the register. Each group is fetched in ONE request:
// nine charts fetched one at a time is nine round trips to Singapore, and
// the person watches skeletons for most of a second.

type BatchItem =
  | { id: ChartId; ok: true; chart: ChartPayload }
  | { id: ChartId; ok: false; message: string }

type Batch = { window: { from: string; to: string; grain: string }; charts: BatchItem[] }

const todayLocal = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date())

export function AnalyticsPage() {
  const [group, setGroup] = useState<GroupId>('money')
  const [preset, setPreset] = useState<Preset>('30')
  const [picks, setPicks] = useState<Picks>({ doctor: null, patient: null, product: null })
  const [filters, setFilters] = useState<AnalyticsFilters>(() => ({
    ...rangeFor('30', todayLocal(), null),
    bucket: 'auto',
  }))

  const defs = useMemo(() => chartsInGroup(group), [group])
  const ids = useMemo(() => defs.map(d => d.id as ChartId), [defs])

  // Which filters any chart in this group actually reads. The bar dims the
  // rest rather than hiding them, so the row does not reshuffle every time
  // the group changes.
  const honoured = useMemo(() => {
    const s = new Set<FilterKey>()
    for (const d of defs) for (const k of d.filters) s.add(k)
    return s
  }, [defs])

  const [data, setData] = useState<Record<string, BatchItem>>({})
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)
  const abort = useRef<AbortController>()

  const query = useMemo(() => {
    const p = new URLSearchParams()
    p.set('charts', ids.join(','))
    if (filters.from) p.set('from', filters.from)
    if (filters.to) p.set('to', filters.to)
    p.set('bucket', filters.bucket)
    for (const k of ['doctorId', 'productId', 'patientId', 'district', 'substance', 'paymentMethod'] as const) {
      const v = filters[k]
      // Only send what a chart on screen will read -- otherwise a stale
      // district would travel with a request whose charts all ignore it.
      if (v && honoured.has(k)) p.set(k, v)
    }
    return p.toString()
  }, [ids, filters, honoured])

  useEffect(() => {
    abort.current?.abort()
    const ac = new AbortController()
    abort.current = ac
    setLoading(true)
    setFailed(null)

    get<Batch>(`/api/analytics/batch?${query}`, ac.signal)
      .then(res => {
        if (ac.signal.aborted) return
        const next: Record<string, BatchItem> = {}
        for (const c of res.charts) next[c.id] = c
        setData(next)
        setLoading(false)
      })
      .catch(e => {
        if (ac.signal.aborted || (e as Error).name === 'AbortError') return
        setFailed((e as Error).message)
        setLoading(false)
      })

    return () => ac.abort()
  }, [query, nonce])

  return (
    <>
      <PageHeader title="Analysis" />
      <div className="content">
        <FilterBar
          filters={filters} picks={picks} preset={preset} honoured={honoured}
          onChange={next => {
            setFilters(next.filters)
            setPicks(next.picks)
            setPreset(next.preset)
          }}
        />

        <div className="viz-tabs" role="tablist" aria-label="Kind of analysis">
          {CHART_GROUPS.map(g => (
            <button
              key={g.id} type="button" role="tab" aria-selected={group === g.id}
              onClick={() => setGroup(g.id)}
            >{g.label}</button>
          ))}
        </div>

        {failed && (
          <div className="card">
            <div className="viz-empty">
              <div>
                <div>{failed}</div>
                <button type="button" className="viz-toggle" style={{ marginTop: 10 }}
                        onClick={() => setNonce(n => n + 1)}>Try again</button>
              </div>
            </div>
          </div>
        )}

        <div className="viz-grid">
          {defs.map(def => {
            const item = data[def.id]
            return (
              <ChartBlock
                key={def.id}
                def={{ title: def.title, question: def.question, span: def.span }}
                payload={!loading && item?.ok ? item.chart : undefined}
                loading={loading}
                error={!loading && item && !item.ok ? item.message : undefined}
                onRetry={() => setNonce(n => n + 1)}
              />
            )
          })}
        </div>

        <p className="dim" style={{ fontSize: 'var(--text-xs)', margin: 0 }}>
          Dates are read in the clinic’s own calendar (Asia/Kolkata). Deleted records and
          cancelled invoices are left out everywhere. Money is reported by invoice date for
          tax and by payment date for what was collected.
        </p>
      </div>
    </>
  )
}
